import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, LogOut, User, Calendar, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_031c01f6-1122-4ae8-87ee-69acab3ae0f1/artifacts/kw4zjnmy_ava.jpeg";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/home");
  };

  const navLinks = [
    { name: "Home", path: "/home" },
    { name: "Packages", path: "/packages" },
    { name: "Customize Tour", path: "/customize-tour" },
    { name: "Blog", path: "/blog" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/95 border-b border-border shadow-md"
          : "backdrop-blur-md bg-white/85 border-b border-white/40 shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/home" className="flex items-center" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Andaman Avalon" className="h-16 w-auto" />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-base font-medium transition-colors duration-200 ${
                  location.pathname === link.path ? "text-primary font-semibold" : "text-muted-foreground hover:text-primary"
                }`}
              >
                {link.name}
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 text-primary font-medium"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border-2 border-secondary" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                  )}
                  <span className="text-sm">Hi, {user.name?.split(" ")[0]}</span>
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)}></div>
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl border border-border overflow-hidden z-50" data-testid="user-menu">
                      <div className="px-4 py-3 border-b bg-muted/40">
                        <p className="font-medium text-primary text-sm">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                      <Link to="/my-bookings" onClick={() => setShowUserMenu(false)} data-testid="nav-my-bookings" className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors text-sm">
                        <Calendar size={16} className="text-secondary" />
                        My Bookings
                      </Link>
                      <Link to="/refer" onClick={() => setShowUserMenu(false)} data-testid="nav-refer" className="flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors text-sm border-t">
                        <Gift size={16} className="text-secondary" />
                        Refer &amp; Earn ₹1,000
                      </Link>
                      <button onClick={() => { setShowUserMenu(false); handleLogout(); }} data-testid="nav-logout-btn" className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors text-sm border-t">
                        <LogOut size={16} className="text-secondary" />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn" className="text-base font-medium text-muted-foreground hover:text-primary">
                  Login
                </Link>
                <Link to="/register" data-testid="nav-signup-btn" className="btn-primary">
                  Sign Up
                </Link>
              </>
            )}
          </div>

          <button data-testid="mobile-menu-toggle" onClick={() => setIsOpen(!isOpen)} className="md:hidden p-2 rounded-md text-primary">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div data-testid="mobile-menu" className="md:hidden backdrop-blur-xl bg-white/90 border-t border-border">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                onClick={() => setIsOpen(false)}
                className={`block text-lg font-medium ${
                  location.pathname === link.path ? "text-primary font-semibold" : "text-muted-foreground"
                }`}
              >
                {link.name}
              </Link>
            ))}

            {isAuthenticated ? (
              <>
                <div className="flex items-center gap-3 pt-2 border-t border-border">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-secondary" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                      {user.name?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <Link to="/my-bookings" onClick={() => setIsOpen(false)} data-testid="mobile-my-bookings" className="flex items-center gap-2 text-lg text-muted-foreground">
                  <Calendar size={18} /> My Bookings
                </Link>
                <Link to="/refer" onClick={() => setIsOpen(false)} data-testid="mobile-refer" className="flex items-center gap-2 text-lg text-muted-foreground">
                  <Gift size={18} /> Refer &amp; Earn
                </Link>
                <button onClick={() => { setIsOpen(false); handleLogout(); }} data-testid="mobile-logout-btn" className="w-full flex items-center gap-2 text-lg text-muted-foreground">
                  <LogOut size={18} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setIsOpen(false)} data-testid="mobile-login-btn" className="block text-lg font-medium text-muted-foreground border-t border-border pt-4">
                  Login
                </Link>
                <Link to="/register" onClick={() => setIsOpen(false)} data-testid="mobile-signup-btn" className="btn-primary block text-center">
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
